import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import ScriptHeader from './components/ScriptHeader';
import ActSceneNavigation from './components/ActSceneNavigation';
import ScriptLine from './components/ScriptLine';
import LineInteractionModal from './components/LineInteractionModal';
import GlossarySidebar from './components/GlossarySidebar';
import ReadingProgressBar from './components/ReadingProgressBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getPlayStructure, 
  getSceneLines, 
  getPlayGlossary,
  getUserBookmarks,
  createBookmark,
  deleteBookmark,
  upsertUserProgress
} from '../../services/playsService';
import QuizPerformanceBreakdown from './components/QuizPerformanceBreakdown';
import useTheme from '../../hooks/useTheme';
import useScrollPersistence from '../../hooks/useScrollPersistence';
import useReadingProgress from '../../hooks/useReadingProgress';

const QUIZ_TYPES = [
  {
    id: 'multiple_choice',
    label: 'Multiple Choice',
    description: 'Answer questions about the scene',
    icon: 'CheckSquare',
    color: 'text-primary',
    bg: 'bg-primary/10 border-primary/30 hover:bg-primary/20',
    activeBg: 'bg-primary/20 border-primary ring-2 ring-primary/40',
  },
  {
    id: 'character_matching',
    label: 'Character Matching',
    description: 'Match characters to their lines',
    icon: 'Users',
    color: 'text-secondary',
    bg: 'bg-secondary/10 border-secondary/30 hover:bg-secondary/20',
    activeBg: 'bg-secondary/20 border-secondary ring-2 ring-secondary/40',
  },
  {
    id: 'pronunciation',
    label: 'Pronunciation',
    description: 'Practice speaking the lines aloud',
    icon: 'Mic',
    color: 'text-accent',
    bg: 'bg-accent/10 border-accent/30 hover:bg-accent/20',
    activeBg: 'bg-accent/20 border-accent ring-2 ring-accent/40',
  },
];

const PlayReadingInterface = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const [playData, setPlayData] = useState(null);
  const [currentSceneLines, setCurrentSceneLines] = useState([]);
  const [glossaryWords, setGlossaryWords] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [currentAct, setCurrentAct] = useState(1);
  const [currentScene, setCurrentScene] = useState(1);
  const [highlightedLineId, setHighlightedLineId] = useState(null);
  const [playingLineId, setPlayingLineId] = useState(null);
  const [selectedGlossaryWord, setSelectedGlossaryWord] = useState(null);
  const [showPerformanceBreakdown, setShowPerformanceBreakdown] = useState(false);
  const [lastBookmark, setLastBookmark] = useState(null);
  const [clickedLineIds, setClickedLineIds] = useState(new Set());
  const [selectedQuizType, setSelectedQuizType] = useState('multiple_choice');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linesLoading, setLinesLoading] = useState(false);

  // Refs map: lineId -> DOM element for smooth scroll-to-bookmark
  const lineRefsMap = useRef({});

  // Get playId from either URL params or navigation state
  const playId = searchParams?.get('playId') || location?.state?.playId;

  // Reading progress bar (scroll-based)
  const readingProgress = useReadingProgress();

  // Scroll persistence: save/restore per play+act+scene
  const sceneContentReady = !linesLoading && currentSceneLines?.length > 0;
  useScrollPersistence(playId, currentAct, currentScene, sceneContentReady);

  // Build set of bookmarked line gids for O(1) lookup
  const bookmarkedLineIds = new Set(
    bookmarks?.map(b => b?.lineId)?.filter(Boolean)
  );

  // Load play structure and glossary on mount
  useEffect(() => {
    if (!playId) {
      setError('No play selected');
      setLoading(false);
      return;
    }

    loadPlayData();
  }, [playId]);

  // Load scene lines when act/scene changes
  useEffect(() => {
    if (playData && currentAct && currentScene) {
      loadSceneLines();
    }
  }, [playData, currentAct, currentScene]);

  // Load bookmarks when user changes
  useEffect(() => {
    if (user?.id && playId) {
      loadBookmarks();
    }
  }, [user?.id, playId]);

  const loadPlayData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [structure, glossary] = await Promise.all([
        getPlayStructure(playId),
        getPlayGlossary(playId)
      ]);

      setPlayData(structure);
      setGlossaryWords(glossary);
      
      // Set initial act and scene to first available
      if (structure?.acts?.length > 0 && structure?.acts?.[0]?.scenes?.length > 0) {
        setCurrentAct(structure?.acts?.[0]?.actNumber);
        setCurrentScene(structure?.acts?.[0]?.scenes?.[0]?.sceneNumber);
      }
    } catch (err) {
      console.error('Error loading play data:', err);
      setError(err?.message || 'Failed to load play');
    } finally {
      setLoading(false);
    }
  };

  const loadSceneLines = async () => {
    try {
      setLinesLoading(true);
      const lines = await getSceneLines(playId, currentAct, currentScene);
      
      // Transform lines to match expected format
      const formattedLines = lines?.map(line => ({
        lineId: line?.line_id,
        gid: line?.gid,
        act: line?.act,
        scene: line?.scene,
        lineInScene: line?.line_in_scene,
        character: line?.character,
        dialogue: line?.dialogue,
        stageDirection: line?.stage_direction,
        is_stage_direction: line?.is_stage_direction,
        hasAudio: line?.hasAudio,
        audioUrl: line?.audioUrl,
        pronunciationHelp: line?.pronunciation_help,
        voiceActor: line?.voiceActor,
        isPlaying: false
      }));

      setCurrentSceneLines(formattedLines);
    } catch (err) {
      console.error('Error loading scene lines:', err);
      setError('Failed to load scene lines');
    } finally {
      setLinesLoading(false);
    }
  };

  const loadBookmarks = async () => {
    try {
      const userBookmarks = await getUserBookmarks(user?.id, playId);
      setBookmarks(userBookmarks);
    } catch (err) {
      console.error('Error loading bookmarks:', err);
    }
  };

  const getCurrentScene = () => {
    if (!playData?.acts) return null;
    
    const act = playData?.acts?.find(a => a?.actNumber === currentAct);
    return act?.scenes?.find(s => s?.sceneNumber === currentScene);
  };

  const handleNavigate = (actNumber, sceneNumber) => {
    setCurrentAct(actNumber);
    setCurrentScene(sceneNumber);
    setHighlightedLineId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLineClick = (line) => {
    if (line?.stageDirection) return;
    setSelectedLine(line);
    setHighlightedLineId(line?.gid || line?.lineId);
    if (line?.hasAudio) {
      setPlayingLineId(line?.gid || line?.lineId);
      setTimeout(() => setPlayingLineId(null), 3000);
    }

    // Track unique lines read
    const lineKey = line?.gid || line?.lineId;
    if (lineKey) {
      setClickedLineIds(prev => {
        if (prev?.has(lineKey)) return prev;
        const next = new Set(prev);
        next?.add(lineKey);
        return next;
      });
    }

    // Track progress: upsert user_progress in Supabase
    if (user?.id && playId && playData?.totalLines > 0) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex?.test(playId)) {
        upsertUserProgress(
          user?.id,
          playId,
          line?.lineInScene || 0,
          playData?.totalLines
        )?.catch(err => console.error('Progress tracking error:', err));
      }
    }
  };

  const handleWordSelect = (word) => {
    setSelectedGlossaryWord(word);
    setIsGlossaryOpen(true);
  };

  const handleAddBookmark = async () => {
    if (!user?.id) {
      setError('You must be logged in to create bookmarks');
      return;
    }

    const firstLine = currentSceneLines?.find(l => !l?.stageDirection);
    if (!firstLine) return;

    try {
      // Find the full line object from database to get its UUID
      const lines = await getSceneLines(playId, currentAct, currentScene);
      const lineToBookmark = lines?.find(l => l?.line_id === firstLine?.lineId);
      
      if (lineToBookmark) {
        await createBookmark(user?.id, lineToBookmark?.id);
        await loadBookmarks(); // Reload bookmarks
        setLastBookmark({
          act: currentAct,
          scene: currentScene,
          lineNumber: firstLine?.lineInScene || 1,
          lineId: firstLine?.gid || firstLine?.lineId
        });
      }
    } catch (err) {
      console.error('Error creating bookmark:', err);
      setError('Failed to create bookmark');
    }
  };

  // Smooth scroll to the bookmarked line's DOM element
  const handleNavigateToBookmark = useCallback((bookmark) => {
    const targetAct = bookmark?.act;
    const targetScene = bookmark?.scene;
    const targetLineId = bookmark?.lineId;

    if (targetAct !== currentAct || targetScene !== currentScene) {
      // Navigate to the correct scene first, then scroll after lines load
      setCurrentAct(targetAct);
      setCurrentScene(targetScene);
      setHighlightedLineId(targetLineId);
      // Scroll will happen via useScrollPersistence restore or manual scroll below
      setTimeout(() => {
        const el = lineRefsMap?.current?.[targetLineId];
        if (el) {
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 400);
    } else {
      setHighlightedLineId(targetLineId);
      const el = lineRefsMap?.current?.[targetLineId];
      if (el) {
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentAct, currentScene]);

  const handleRemoveBookmark = async (bookmarkId) => {
    try {
      await deleteBookmark(bookmarkId);
      await loadBookmarks(); // Reload bookmarks
    } catch (err) {
      console.error('Error removing bookmark:', err);
      setError('Failed to remove bookmark');
    }
  };

  const calculateProgress = () => {
    if (!playData?.acts) return 0;
    
    const totalScenes = playData?.acts?.reduce((sum, act) => sum + act?.scenes?.length, 0);
    const completedScenes = playData?.acts?.slice(0, currentAct - 1)?.reduce((sum, act) => sum + act?.scenes?.length, 0) + (currentScene - 1);
    return Math.round((completedScenes / totalScenes) * 100);
  };

  const currentSceneData = getCurrentScene();

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background'}`}>
        <RoleBasedNavigation />
        <div className="main-content flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading play...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !playData) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background'}`}>
        <RoleBasedNavigation />
        <div className="main-content flex items-center justify-center">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Play</h2>
            <p className="text-muted-foreground mb-4">{error || 'Play not found'}</p>
            <Button onClick={() => navigate('/student-plays-library')}>
              Back to Library
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-background text-foreground'}`}>
      {/* Fixed reading progress bar */}
      <ReadingProgressBar progress={readingProgress} isDark={isDark} />
      <RoleBasedNavigation />
      <div className="main-content">
        <BreadcrumbTrail 
          customBreadcrumbs={[
            { label: 'Stage', path: '/student-stage-dashboard' },
            { label: playData?.title, path: '/play-reading-interface' },
            { label: `Act ${currentAct}, Scene ${currentScene}`, path: '/play-reading-interface' }
          ]}
        />

        <ScriptHeader
          playTitle={playData?.title}
          currentAct={currentAct}
          currentScene={currentScene}
          onBackToDashboard={() => navigate('/student-stage-dashboard')}
          onToggleGlossary={() => setIsGlossaryOpen(!isGlossaryOpen)}
          isGlossaryOpen={isGlossaryOpen}
          linesRead={clickedLineIds?.size}
          totalLines={playData?.totalLines || 0}
        />

        <ActSceneNavigation
          acts={playData?.acts}
          currentAct={currentAct}
          currentScene={currentScene}
          onNavigate={handleNavigate}
          readingProgress={calculateProgress()}
        />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-12">
              {/* Bookmarks and Quick Tips on same line for desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* Left Column - Bookmarks with buttons */}
                <div className="flex flex-col gap-4">
                  {lastBookmark && (
                    <div className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors duration-300 ${isDark ? 'bg-amber-950/30 border-amber-700/50' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon name="Bookmark" size={15} className="text-amber-500 fill-amber-400 flex-shrink-0" />
                        <span className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                          Bookmarked — Act {lastBookmark?.act}, Scene {lastBookmark?.scene}, Line {lastBookmark?.lineNumber}
                        </span>
                      </div>
                      <button
                        onClick={() => handleNavigateToBookmark(lastBookmark)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors duration-150 ${isDark ? 'bg-amber-700 hover:bg-amber-600 text-amber-100' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                      >
                        <Icon name="ArrowRight" size={13} />
                        Jump to Bookmarked Line
                      </button>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    iconName="BookmarkPlus"
                    iconPosition="left"
                    onClick={handleAddBookmark}
                    fullWidth
                    disabled={!user?.id}
                  >
                    Bookmark This Scene
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    iconName="BarChart3"
                    iconPosition="left"
                    onClick={() => setShowPerformanceBreakdown(true)}
                    fullWidth
                    disabled={!user?.id}
                  >
                    View Performance Breakdown
                  </Button>
                </div>

                {/* Right Column - Quick Tips */}
                <div className="flex flex-col">
                  <div className={`border border-border rounded-lg p-4 flex-1 flex flex-col transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Info" size={18} className="text-accent" />
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-foreground'}`}>Quick Tips</h3>
                      </div>
                      {/* Theme Toggle */}
                      <button
                        onClick={toggleTheme}
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors duration-200 border ${
                          isDark
                            ? 'bg-gray-800 border-gray-600 text-amber-300 hover:bg-gray-700' :'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                        }`}
                      >
                        <Icon name={isDark ? 'Sun' : 'Moon'} size={13} />
                        {isDark ? 'Light' : 'Dark'}
                      </button>
                    </div>
                    <ul className="space-y-2 text-xs text-muted-foreground flex-1">
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Click any line to see details and hear pronunciation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Lines with audio have a headphone icon</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Underlined words have glossary definitions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Use bookmarks to save your reading position</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Your scroll position is saved automatically per scene</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-12">
              <div className={`border border-border rounded-lg overflow-hidden transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card'}`}>
                <div className="bg-gradient-to-r from-primary to-secondary p-6 text-primary-foreground">
                  <h2 className="text-2xl font-semibold mb-2">
                    Act {currentAct}, Scene {currentScene}
                  </h2>
                  <p className="text-sm opacity-90">{currentSceneData?.setting}</p>
                </div>

                <div className="p-6">
                  {linesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Icon name="Loader" size={32} className="text-primary animate-spin" />
                    </div>
                  ) : (
                    <div className={`space-y-1 ${isDark ? 'reading-dark' : ''}`}>
                      {currentSceneLines?.map((line) => {
                        const lineKey = line?.gid || line?.lineId;
                        return (
                          <ScriptLine
                            key={lineKey}
                            line={{
                              ...line,
                              isPlaying: playingLineId === lineKey
                            }}
                            onLineClick={handleLineClick}
                            isHighlighted={highlightedLineId === lineKey}
                            isBookmarked={bookmarkedLineIds?.has(lineKey)}
                            isDark={isDark}
                            glossaryTerms={glossaryWords}
                            lineRef={(el) => {
                              if (el) lineRefsMap.current[lineKey] = el;
                              else delete lineRefsMap?.current?.[lineKey];
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className={`border-t p-6 transition-colors duration-300 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-border bg-muted/30'}`}>
                  <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Icon name="Headphones" size={16} className="text-secondary" />
                        <span className="text-sm text-muted-foreground">
                          {currentSceneLines?.filter(l => l?.hasAudio)?.length} audio lines
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="MessageSquare" size={16} className="text-accent" />
                        <span className="text-sm text-muted-foreground">
                          {currentSceneLines?.filter(l => !l?.stageDirection)?.length} dialogue lines
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Start Quiz Panel */}
                  <div className={`rounded-xl border p-5 transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-card border-border'}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="Award" size={20} className="text-primary" />
                      <h3 className={`text-base font-semibold ${isDark ? 'text-gray-100' : 'text-foreground'}`}>
                        Test Your Knowledge
                      </h3>
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-primary/20 text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
                        Act {currentAct} · Scene {currentScene}
                      </span>
                    </div>

                    <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                      Choose a quiz type to test what you've learned from this scene:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                      {QUIZ_TYPES?.map((qt) => (
                        <button
                          key={qt?.id}
                          onClick={() => setSelectedQuizType(qt?.id)}
                          className={`flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-all duration-200 ${
                            selectedQuizType === qt?.id ? qt?.activeBg : qt?.bg
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon name={qt?.icon} size={16} className={qt?.color} />
                            <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-foreground'}`}>
                              {qt?.label}
                            </span>
                            {selectedQuizType === qt?.id && (
                              <Icon name="CheckCircle" size={14} className={qt?.color + ' ml-auto'} />
                            )}
                          </div>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                            {qt?.description}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Play"
                        iconPosition="left"
                        onClick={() => {
                          const params = new URLSearchParams({
                            playId: playId || '',
                            act: String(currentAct),
                            scene: String(currentScene),
                            quizType: selectedQuizType,
                          });
                          navigate(`/quiz-interface?${params?.toString()}`);
                        }}
                        disabled={!playId}
                      >
                        Start Quiz
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        iconName="BarChart3"
                        iconPosition="left"
                        onClick={() => setShowPerformanceBreakdown(true)}
                        disabled={!user?.id}
                      >
                        View Performance
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedLine && (
        <LineInteractionModal
          line={selectedLine}
          onClose={() => {
            setSelectedLine(null);
            setPlayingLineId(null);
          }}
          onWordSelect={handleWordSelect}
          glossaryWords={glossaryWords}
        />
      )}
      <GlossarySidebar
        glossaryWords={glossaryWords}
        isOpen={isGlossaryOpen}
        onClose={() => setIsGlossaryOpen(false)}
        selectedWord={selectedGlossaryWord}
      />
      {showPerformanceBreakdown && (
        <QuizPerformanceBreakdown
          playId={playId}
          onClose={() => setShowPerformanceBreakdown(false)}
        />
      )}
    </div>
  );
};

export default PlayReadingInterface;